import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { base64StringToBlob } from 'blob-util';
import Swal from 'sweetalert2';
import useFetch from '../../hooks/useFetch';

const thumbsContainer = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16,
    overflowY: 'auto',
    maxHeight: 280
};

const thumb = {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2,
    border: '1px solid #eaeaea',
    marginBottom: 8,
    marginRight: 8,
    width: 100,
    height: 100,
    padding: 4,
    boxSizing: 'border-box'
};

const thumbInner = {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative'
};

const img = {
    display: 'block',
    width: 'auto',
    height: '100%'
};

const delete_icon = {
    position: 'absolute',
    right: '10px',
    top: '10px',
    cursor: 'pointer',
    fontSize: '20px',
    color: 'white',
    backgroundColor: 'black',
    borderRadius: '50%',
    stroke: 'black',
    strokeWidth: '30px'
};

/**
 * 
 * @param { fotosDescritivasAdicionadas: fotosDescritivasAdicionadas,
 *          fotosDescritivasSalvas: this.state.fotosDescritivas.salvas,
 *          acao: state.acao,
 *          ImageDropzoneRef: ImageDropzoneRef } props 
 * @returns 
 */
const FotosDescritivasDropzone = forwardRef((props, ref) => {
    const { del } = useFetch();

    const defaultState = {
        files: {
            salvas: [],
            adicionadas: []
        },
    };

    useImperativeHandle(ref, () => ({
        reloadImages(images) {
            if (Array.isArray(images) && !images.length) {
                let filesData = [];
                for (const file of images) {
                    let blobImage = base64StringToBlob(file.base64, 'image/png');
                    filesData.push({
                        ...file,
                        value: blobImage,
                        preview: URL.createObjectURL(file)
                    });
                }
                return filesData;
            }
        }
    }));

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => {
            if (!files)
                return null;
            files.salvas.forEach(file => URL.revokeObjectURL(file.preview))
            files.adicionadas.forEach(file => URL.revokeObjectURL(file.preview))
        }
    }, [props]);

    // props recebe os parâmetros do componente pai (Item.js) quando faz <ImageDropzone (parâmetros) />
    const fotosDescritivasAdicionadas = props.fotosDescritivasAdicionadas;
    const [files, setFiles] = useState(() => {
        let fotosDescritivas = { salvas: [], adicionadas: [] };
        if (props.acao !== "Criar") {
            if (Array.isArray(props.fotosDescritivasSalvas) && props.fotosDescritivasSalvas.length > 0) {
                for (const file of props.fotosDescritivasSalvas) {
                    let blobImage = base64StringToBlob(file.base64, 'image/png');
                    fotosDescritivas.salvas.push({
                        ...file,
                        value: blobImage,
                        preview: URL.createObjectURL(blobImage)
                    });
                }
            }
        } return fotosDescritivas;
    });

    const { getRootProps, getInputProps } = useDropzone({
        disabled: props.acao === "Visualizar",
        accept: {
            'image/*': []
        },
        onDrop: acceptedFiles => {
            (async () => {
                let imagensCarregadas = [];
                for (const file of acceptedFiles) {
                    let readFile = await readFileData(file);
                    imagensCarregadas.push({
                        value: file,
                        preview: URL.createObjectURL(file)
                    });
                    let imagensAtualizadas = {
                        salvas: [...files.salvas],
                        adicionadas: [...files.adicionadas, ...imagensCarregadas]
                    };
                    setFiles(imagensAtualizadas);
                    fotosDescritivasAdicionadas(imagensAtualizadas); // Passando o valor para o componente pai (Item.js)
                }
            })();
        }
    });

    const readFileData = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
            resolve(reader.result);
        };
        reader.onerror = function () {
            reject(reader.error);
        };
        reader.readAsBinaryString(file);
    });

    const excluirFotoAdicionada = (index) => {
        let fotosAdicionadas = [...files.adicionadas];
        fotosAdicionadas.splice(index, 1);
        let imagensAtualizadas = {
            salvas: [...files.salvas],
            adicionadas: fotosAdicionadas
        }
        setFiles(imagensAtualizadas);
        fotosDescritivasAdicionadas(imagensAtualizadas); // Passando o valor para o componente pai (Item.js)
    }

    const excluirFotoSalva = (index) => {
        let imagensAtualizadas = {};
        Swal.fire({
            title: 'Deseja excluir a imagem?',
            text: "Esta ação não poderá ser desfeita!",
            icon: 'warning',
            showCancelButton: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                // Excluir a imagem do banco de dados
                let foto = files.salvas[index];
                let result = await del(`/fotodescritiva/${foto.id_item}/${foto.sec_fotodescritiva}`);

                if (result.error) {
                    Swal.fire(
                        'Erro!',
                        result.message,
                        'error'
                    );
                    return;
                }

                let fotosSalvas = [...files.salvas];
                fotosSalvas.splice(index, 1);
                imagensAtualizadas = {
                    salvas: fotosSalvas,
                    adicionadas: [...files.adicionadas]
                }
                setFiles(imagensAtualizadas);
                fotosDescritivasAdicionadas(imagensAtualizadas); // Passando o valor para o componente pai (Item.js)

                Swal.fire(
                    'Imagem excluída!',
                    'A imagem foi excluída com sucesso.',
                    'success'
                );
            }
        });
    }

    return (
        <>
            <section className="container p-0 overflow-auto">
                <div {...getRootProps({ className: 'dropzone' })}>
                    <input {...getInputProps()} />
                    <div className="p-5 bg-light">Arraste as imagens para cá ou clique aqui para selecionar</div>
                </div>
                <aside style={thumbsContainer}>
                    {
                        files ?
                            files.adicionadas.map((file, index) => (
                                <div style={thumb} key={index}>
                                    <div style={thumbInner} key={index}>
                                        <img
                                            key={index}
                                            src={file.preview}
                                            style={img}
                                            alt={"Imagem"}
                                            // Revoke data uri after image is loaded
                                            onLoadedData={() => { URL.revokeObjectURL(file.preview) }}
                                        />
                                        {
                                            props.acao !== "Visualizar" ?
                                                <FontAwesomeIcon
                                                    icon={faXmarkCircle}
                                                    style={delete_icon}
                                                    onClick={() => excluirFotoAdicionada(index)}>
                                                </FontAwesomeIcon>
                                                : null
                                        }
                                    </div>
                                </div>
                            )) : null
                    }
                    {
                        files ?
                            files.salvas.map((file, index) => (
                                <div style={thumb} key={index}>
                                    <div style={thumbInner} key={index}>
                                        <img
                                            key={index}
                                            src={file.preview}
                                            style={img}
                                            alt={"Imagem"}
                                            // Revoke data uri after image is loaded
                                            onLoadedData={() => { URL.revokeObjectURL(file.preview) }}
                                        />
                                        {
                                            props.acao !== "Visualizar" ?
                                                <FontAwesomeIcon
                                                    icon={faXmarkCircle}
                                                    style={delete_icon}
                                                    onClick={() => excluirFotoSalva(index)}>
                                                </FontAwesomeIcon>
                                                : null
                                        }
                                    </div>
                                </div>
                            ))
                            : null
                    }
                </aside>
            </section>
        </>
    );
});

export default FotosDescritivasDropzone;